import React, { useState, useEffect } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import DataTable from "react-data-table-component";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

//Redux
import { useSelector, useDispatch } from "react-redux"; //
//Components
import Layout from "../../../../Layout/Layout";
import Constants from "../../../../common/SupplierConstants";
import { colData } from "./ReportTableColumns";
import { loadSupplierReport } from "../../../../redux/feature/reportSlice";
import CommonFilter from "../CommonFilter";
import ItemServiceV1 from "../../../../services/customer_appCommonService";

// Only queries listed here get a default date floor + max user range.
// Every other report card is unaffected (filterCfg = null).
const REPORT_FILTER_CONFIG = {
  ALL_ERF_EXT_REPORT: {
    dateField: "createdAt", // raw ISO string in pipeline → string $gte works
    dateOnly: false,
    defaultFloorMonths: 6, // initial load = last 6 months
    maxRangeMonths: 6, // user may pick at most a 6-month window
  },
};

// ALL_ERF_EXT_REPORT: these header fields show ONCE per documentNumber group.
// Item fields (material*, qty, rate, tax, godown, days...) are NOT listed → always show.
const ERF_HEADER_FIELDS = [
  // "sourceType",
  // "documentNumber",
  // "erfType",
  // "orderType",
  // "orderDate",
  // "createdAt",
  // "salePoint",
  // "costCenter",
  "cartage",
  // "doctorName",
  // "drType",
  // "drCode",
  // "patientName",
  // "age",
  // "patientChallenges",
  // "deliveryAddress",
  // "deliveryState",
  // "deliveryPinCode",
  // "mobileNr",
  // "email",
  // "caregiverMobileNr",
  // "erfStartDate",
  // "erfEndDate",
  // "earlyClosureDate",
  // "creditNoteDate",
  "returnedAmount",
  "totalCreditNote",
];

// Type-safe date condition — matches createdAt whether stored as String OR Date.
const buildDateExpr = (field, fromISO, toISO) => {
  const val = {
    $convert: { input: `$${field}`, to: "date", onError: null, onNull: null },
  };
  const conds = [];
  if (fromISO) conds.push({ $gte: [val, { $toDate: fromISO }] });
  if (toISO) conds.push({ $lte: [val, { $toDate: toISO }] });
  if (!conds.length) return {};
  return { $expr: conds.length === 1 ? conds[0] : { $and: conds } };
};

// Default floor: from N months ago → now (open-ended, type-safe).
const buildFloorFilter = (cfg) => {
  if (!cfg) return {};
  const from = new Date();
  from.setMonth(from.getMonth() - cfg.defaultFloorMonths);
  return buildDateExpr(cfg.dateField, from.toISOString(), null);
};

// Converts CommonFilter's plain date range into the type-safe $expr form,
// making the end bound inclusive of the whole day.
const normalizeFilters = (filters, cfg) => {
  if (!cfg) return filters;
  const out = { ...filters };
  const df = cfg.dateField;
  const range = out[df];
  if (range && typeof range === "object" && (range.$gte || range.$lte)) {
    delete out[df];
    const fromISO = range.$gte
      ? range.$gte.length === 10
        ? `${range.$gte}T00:00:00.000Z`
        : range.$gte
      : null;
    const toISO = range.$lte
      ? range.$lte.length === 10
        ? `${range.$lte}T23:59:59.999Z`
        : range.$lte
      : null;
    Object.assign(out, buildDateExpr(df, fromISO, toISO));
  }
  return out;
};

const SupplierReportsDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  localStorage.setItem("menu", "Report");
  localStorage.setItem("submenu", "-");
  let reportInfo = useSelector((state) => state?.report); // Report Details from Store
  let reportData = useSelector((state) => state?.report?.supplierReport); // Report Details from Store
  let reportDatapagination = useSelector(
    (state) => state?.report?.supplierReport?.pagination,
  );
  const [tableData, setTableData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [getNewPerPage, setNewPerPage] = useState(100);
  const [isCardVisible2, setCardVisible2] = useState(true);

  const [filterShow, setFilterShow] = useState(false);

  // const [getFilter, setFilter] = useState({});

  const queryName = reportInfo?.data?.queryName;
  const filterCfg = REPORT_FILTER_CONFIG[queryName] || null;

  const [getFilter, setFilter] = useState(() => buildFloorFilter(filterCfg));

  const displayData = React.useMemo(() => {
    // Only group this report; all other report cards render normally.
    if (queryName !== "ALL_ERF_EXT_REPORT") return tableData;

    let prevDoc = null;
    return (tableData || []).map((row) => {
      const isFirst = row.documentNumber !== prevDoc;
      prevDoc = row.documentNumber;
      if (isFirst) return row; // first row of a group → show everything
      const blanked = { ...row };
      ERF_HEADER_FIELDS.forEach((f) => {
        blanked[f] = "";
      }); // repeated rows → blank header fields
      return blanked;
    });
  }, [tableData, queryName]);

  const toggleCardVisibility2 = () => {
    setCardVisible2(!isCardVisible2);
  };

  // const loadReport = async () => {
  //   if (reportInfo?.data) {
  //     let obj = {
  //       queryName: reportInfo?.data?.queryName,
  //       page: 1,
  //       per_page: 100,
  //     };
  //     const toastId = toast.loading("Loading...", {
  //       position: "top-right",
  //     });

  //     dispatch(loadSupplierReport(obj));
  //     toast.dismiss(toastId);
  //   } else {
  //     window.history.back();
  //   }
  // };

  const loadReport = async () => {
    if (reportInfo?.data) {
      const floor = buildFloorFilter(filterCfg);
      setFilter(floor);

      let obj = {
        queryName: reportInfo?.data?.queryName,
        page: 1,
        per_page: 100,
        ...(Object.keys(floor).length && { rowFilter: { $and: [floor] } }),
      };
      const toastId = toast.loading("Loading...", {
        position: "top-right",
      });

      dispatch(loadSupplierReport(obj));
      toast.dismiss(toastId);
    } else {
      window.history.back();
    }
  };

  useEffect(() => {
    if (reportInfo?.data?.queryName) {
      const reportCol = colData.find(
        (data) => data.name === reportInfo?.data?.queryName,
      );
      if (reportCol) {
        setColumns(reportCol.columns);
      }
      loadReport();
      // getMasterData();
    } else {
      navigate(Constants.SUPPLIER_REPORTS);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setTableData(reportData.data);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportData]);

  // const handleAdvanceSearchFuncApi = async (filters) => {
  //   setFilter(filters);
  //   let obj = {
  //     queryName: reportInfo?.data?.queryName,
  //     // module: "CUSTOMER",
  //     page: 1,
  //     per_page: getNewPerPage,
  //     searchTerm: "",
  //     rowFilter: {
  //       $and: [filters],
  //     },
  //   };
  //   dispatch(loadSupplierReport(obj));
  //   setTableData(reportData?.data);
  // };

  const handleAdvanceSearchFuncApi = async (filters) => {
    const normalized = normalizeFilters(filters, filterCfg);

    // user picked a date if createdAt ($expr) OR orderDate is present
    const userPickedDate =
      filterCfg && (normalized.$expr || normalized.orderDate);

    const merged =
      filterCfg && !userPickedDate
        ? { ...buildFloorFilter(filterCfg), ...normalized } // no date → keep floor
        : normalized; // date picked → user bounds it

    setFilter(merged);
    let obj = {
      queryName: reportInfo?.data?.queryName,
      page: 1,
      per_page: getNewPerPage,
      searchTerm: "",
      rowFilter: { $and: [merged] },
    };
    dispatch(loadSupplierReport(obj));
  };

  // const handleClear = () => {
  //   setFilter({});
  //   loadReport(); // reload without filter
  // };

  const handleClear = () => {
    setFilter(buildFloorFilter(filterCfg));
    loadReport(); // reload back to the default floor
  };

  const handlePageChange = (page) => {
    let obj = {};
    const filterCheck = Object.keys(getFilter);
    if (filterCheck.length > 0) {
      obj = {
        queryName: reportInfo?.data?.queryName,
        page: page,
        per_page: getNewPerPage,
        rowFilter: {
          $and: [getFilter],
        },
      };
    } else {
      obj = {
        queryName: reportInfo?.data?.queryName,
        page: page,
        per_page: getNewPerPage,
      };
    }
    dispatch(loadSupplierReport(obj));
    setTableData(reportData?.data);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    let obj = {};
    const filterCheck = Object.keys(getFilter);
    if (filterCheck.length > 0) {
      obj = {
        queryName: reportInfo?.data?.queryName,
        page: page,
        per_page: newPerPage,
        rowFilter: {
          $and: [getFilter],
        },
      };
    } else {
      obj = {
        queryName: reportInfo?.data?.queryName,
        page: page,
        per_page: newPerPage,
      };
    }

    setNewPerPage(newPerPage);
    dispatch(loadSupplierReport(obj));
    setTableData(reportData?.data);
  };

  //download xl
  const handleDownloadExcel = async () => {
    const filterCheck = Object.keys(getFilter).length > 0;
    let obj = {
      queryName: reportInfo?.data?.queryName,
      page: 1,
      per_page: 10000,
      ...(filterCheck && {
        rowFilter: { $and: [getFilter] },
      }),
    };
    await ItemServiceV1.erfDownloadExcel(obj).then((item) => {
      const url = window.URL.createObjectURL(new Blob([item]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Export_${reportInfo?.data?.queryName}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    });
  };

  return (
    <main className="dashboard main" id="main">
      <Layout />
      <Row className="justify-center subContent">
        <Col xs={12} md={12} className="rightSide">
          <div className="rightSideInner PageCommonCSS">
            <h2 className="mainTitle">{reportInfo?.data?.name}</h2>
            <p className="subText">Complete Details of the Reports</p>
            <hr />
            <Col
              xs={6}
              md={{ span: 2, offset: 10 }}
              lg={{ span: 2, offset: 10 }}
            >
              <span
                className="filterIcon"
                onClick={() => setFilterShow(!filterShow)}
              >
                <i class="bi bi-filter"></i>
              </span>
            </Col>
            <Row>
              <Col xs={12} md={12} lg={12}>
                {filterShow && (
                  <CommonFilter
                    getfiltereddata={columns}
                    maxDateRangeMonths={filterCfg?.maxRangeMonths || null}
                    cancelAction={() => {
                      setFilterShow(!filterShow);
                      handleClear();
                    }}
                    onSubmit={(filters) => {
                      // console.log(">>Received filters from child:", filters);
                      // const filterData=[filters]
                      handleAdvanceSearchFuncApi(filters);
                      // You can now use the filters here
                    }}
                  />
                )}
              </Col>
            </Row>

            {/* Datatable Section */}
            <div className="accordion" id="accordionExample">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <Row className="accordion-button">
                    <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                      <i
                        className={`bi ${
                          isCardVisible2
                            ? "bi-chevron-down"
                            : "bi-chevron-right"
                        }`}
                        onClick={toggleCardVisibility2}
                      ></i>
                    </Col>
                    <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                      <span className="iconLogo">R</span>
                    </Col>
                    <Col xs={6} md={4} lg={4}>
                      <span className="headerTitle"> Reports</span>
                      <span className="subHeaderTitle">Reports and Status</span>
                    </Col>
                    <Col xs={12} md={7} lg={7} className="">
                      <Row>
                        <Col xs={2} md={1} lg={1} className="">
                          <div className="filter">
                            <span
                              className="icon hide headerIcon"
                              data-bs-toggle="dropdown"
                              aria-expanded="true"
                            >
                              <i className="bi bi-three-dots"></i>
                            </span>
                            <ul
                              className="dropdown-menu dropdown-menu-end dropdown-menu-arrow hide menuList"
                              data-popper-placement="bottom-end"
                            >
                              <li className="dropdown-header text-start">
                                <h6>Menu</h6>
                              </li>
                              <li>
                                <span
                                  className="dropdown-item headerIcon"
                                  onClick={handleDownloadExcel}
                                >
                                  <i className="bi bi-download mr-2"></i>
                                  Download Excel
                                </span>
                              </li>
                              {/* <li>
                                <span
                                  className="dropdown-item headerIcon"
                                  onClick={handleDownloadPDF}
                                >
                                  <i className="bi bi-download mr-2"></i>
                                  Download PDF
                                </span>
                              </li> */}
                            </ul>
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </h2>

                <div
                  // id="acc"
                  className={`accordion-collapse collapse show ${
                    isCardVisible2 ? "visible" : ""
                  }`}
                  // aria-labelledby="headingOne"
                  // data-bs-parent="#accordionExample"
                >
                  <div className="accordion-body">
                    <Row>
                      <Col xs={12} md={12} lg={12}>
                        <DataTable
                          title=""
                          columns={columns}
                          // data={tableData}
                          data={displayData}
                          progressPending={reportData?.status}
                          paginationRowsPerPageOptions={
                            Constants.ROW_REPORT_PER_PAGE
                          }
                          pagination
                          paginationServer
                          paginationPerPage={100}
                          paginationTotalRows={reportDatapagination?.total}
                          onChangeRowsPerPage={handlePerRowsChange}
                          onChangePage={handlePageChange}
                        />
                      </Col>
                    </Row>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </main>
  );
};

export default SupplierReportsDetails;
